import {LocalStorageKey, fromLocalStorage, removeFromLocalStorage, toLocalStorage} from 'Shared/utilities/localStorage';
import React, {useCallback, useEffect, useRef, useState} from 'react';

export const useInactivityTimer = (
  onLogout: () => void,
  inactivityWarningTime: number,
  logoutTimeAfterWarning: number
) => {
  const [isInactive, setIsInactive] = useState(false);
  const [showWarning, setShowWarning] = useState(false);
  const [countdown, setCountdown] = useState(logoutTimeAfterWarning / 1000);
  const [lastActiveTime, setLastActiveTime] = useState(Date.now());
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const resetInactivityTimer = useCallback(() => {
    toLocalStorage(LocalStorageKey.LastActive)(Date.now())();
    setLastActiveTime(Date.now());
    setIsInactive(false);
    setShowWarning(false);
    setCountdown(logoutTimeAfterWarning / 1000);

    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    timerRef.current = setTimeout(() => {
      setShowWarning(true);
      setIsInactive(true);
    }, inactivityWarningTime);
  }, [inactivityWarningTime, logoutTimeAfterWarning]);

  const syncUserActivityAcrossTabs = () => {
    const lastActiveTimeStored = fromLocalStorage(LocalStorageKey.LastActive)();
    if (lastActiveTimeStored && Date.now() - parseInt(lastActiveTimeStored) < inactivityWarningTime) {
      setIsInactive(false);
      setShowWarning(false);
    }
  };

  const handleUserActivity = useCallback(() => {
    resetInactivityTimer();
  }, [resetInactivityTimer]);

  const handleStorageEvent = (e: StorageEvent) => {
    if (e.key === LocalStorageKey.LastActive) {
      syncUserActivityAcrossTabs();
    }
    // // if localStorage is cleared, then logout
    if (e.key === null) {
      onLogout();
    }
  };

  useEffect(() => {
    const activityEvents = ['load', 'mousemove', 'touchmove', 'mousedown', 'click', 'scroll', 'keypress'];
    activityEvents.forEach(event => window.addEventListener(event, handleUserActivity));
    resetInactivityTimer();

    window.addEventListener('storage', handleStorageEvent);
    syncUserActivityAcrossTabs();

    return () => {
      activityEvents.forEach(event => window.removeEventListener(event, handleUserActivity));
      window.removeEventListener('storage', handleStorageEvent);
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      removeFromLocalStorage(LocalStorageKey.LastActive)();
    };
  }, [resetInactivityTimer]);

  useEffect(() => {
    let countdownTimer: NodeJS.Timeout;
    if (showWarning && countdown > 0) {
      countdownTimer = setInterval(() => {
        setCountdown(prev => prev - 1);
      }, 1000);
    }

    return () => {
      clearInterval(countdownTimer);
    };
  }, [showWarning, countdown]);

  useEffect(() => {
    if (countdown === 0) {
      onLogout();
    }
  }, [countdown, onLogout]);

  return {isInactive, showWarning, countdown, lastActiveTime, resetInactivityTimer};
};
